/**
 * Native app-open ping — the Welcome Coffee campaign's grant trigger
 * (Business Rule #45).
 *
 * Tells the backend "this account is on the native app" so it can stamp
 * `users.app_first_seen_at` and, for a qualifying account, grant the one-time
 * welcome coffee. Fires once per JS session, native only, after auth resolves.
 *
 * Web is skipped entirely — the whole point of the campaign is that the PWA
 * doesn't qualify — but the backend re-checks the platform anyway, so this is a
 * bandwidth optimisation, not the security boundary.
 *
 * Deliberately fire-and-forget: a failure here must never surface to the
 * customer or delay app launch. `create_order` carries a server-side backstop
 * for the case where a fresh install is still running the store binary's JS
 * bundle and doesn't have this call yet.
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getClientPlatform } from '../utils/nativePayment';
import { toast } from '../utils/toastPolicy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level so a re-mount (route change, StrictMode double-mount) doesn't
// re-ping. The endpoint is idempotent; this just avoids the noise.
let pinged = false;

export default function useAppSessionPing() {
  const { user, token } = useAuth();
  const firedRef = useRef(false);

  useEffect(() => {
    if (pinged || firedRef.current) return;
    if (!user || !token) return;
    const platform = getClientPlatform();
    if (platform !== 'android' && platform !== 'ios') return;
    firedRef.current = true;
    pinged = true;

    let branch_id = '';
    try {
      branch_id = JSON.parse(localStorage.getItem('selectedBranch') || 'null')?.id || '';
    } catch { /* no branch chosen yet — the backend resolves reward items without it */ }

    axios
      .post(`${API}/app/session`, { platform, branch_id }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        if (!data?.granted) return;
        // Owner decision (2026-08-21): NO auto-opening picker on first launch —
        // the offer lives in the Offers section and the customer goes there at
        // their own pace. This toast is the only immediate on-screen ack; the
        // durable delivery is the push + in-app bell (Business Rule #13), and
        // the persistent surfaces are the Home strip, the Offers card, and the
        // Offers-tab dot.
        toast.success("Welcome! Your first coffee is on us — pick it in Offers");
      })
      .catch(() => { /* silent: the reward still grants on the next launch */ });
  }, [user, token]);
}
